import { use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';

import ReadDevicesResponse from 'features/wandering-boundary-settings/domain/models/read-devices-response';
import ReadDomipDevicesResponse from 'features/wandering-boundary-settings/domain/models/read-domip-devices-response';
import WanderingBoundary from 'features/wandering-boundary-settings/domain/models/wandering-boundary';
import WanderingBoundaryLocation from 'features/wandering-boundary-settings/domain/models/wandering-boundary-location';
import WanderingBoundaryPassage from 'features/wandering-boundary-settings/domain/models/wandering-boundary-passage';
import {
  useCreateWanderingBoundary,
  useCreateWanderingBoundaryPassage,
  useLazyReadDevices,
  useLazyReadDomipDevices,
  useLazyReadWanderingBoundaries,
  useLazyReadWanderingBoundaryPassages,
  useRemoveWanderingBoundary,
  useRemoveWanderingBoundaryPassage,
  useUpdateWanderingBoundary,
  useUpdateWanderingBoundaryPassage,
} from 'features/wandering-boundary-settings/domain/queries/wandering-boundary-settings-queries';

export type WanderingBoundarySettingsDetailsState = {
  wanderingBoundaries: WanderingBoundary[] | undefined;
  selectedLocation: WanderingBoundaryLocation | undefined;
  toggleDetails: (location: WanderingBoundaryLocation) => void;
  isLoading: boolean;
  addWanderingBoundary: () => void;
  removeWanderingBoundary: (wanderingBoundary: WanderingBoundary) => void;
  createWanderingBoundary: (wanderingBoundary: WanderingBoundary) => void;
  updateWanderingBoundary: (wanderingBoundary: WanderingBoundary) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  setShouldShowConfirmationPopup: (shouldShow: boolean) => void;
  wanderingBoundaryPassages: WanderingBoundaryPassage[] | undefined;
  addWanderingBoundaryPassage: () => void;
  removeWanderingBoundaryPassage: (wanderingBoundaryPassage: WanderingBoundaryPassage) => void;
  createWanderingBoundaryPassage: (wanderingBoundaryPassage: WanderingBoundaryPassage, onError?: () => void) => void;
  updateWanderingBoundaryPassage: (wanderingBoundaryPassage: WanderingBoundaryPassage, onError?: () => void) => void;
  detectionLoopDevices: ReadDevicesResponse[] | undefined;
  domipDevices: ReadDomipDevicesResponse[] | undefined;
};

const useWanderingBoundarySettingsDetailsState = (): WanderingBoundarySettingsDetailsState => {
  const { t } = useTranslation('wanderingBoundarySettings');
  const [selectedLocation, setSelectedLocation] = useState<WanderingBoundaryLocation | undefined>();
  const [wanderingBoundaries, setWanderingBoundaries] = useState<WanderingBoundary[] | undefined>();
  const [isEditing, setIsEditing] = useState(false);
  const [shouldShowConfirmationPopup, setShouldShowConfirmationPopup] = useState(false);
  const [wanderingBoundaryPassages, setWanderingBoundaryPassages] = useState<WanderingBoundaryPassage[] | undefined>();
  const [detectionLoopDevices, setdetectionLoopDevices] = useState<ReadDevicesResponse[] | undefined>();
  const [domipDevices, setDomipDevices] = useState<ReadDevicesResponse[] | undefined>();

  const { showConfirmationPopUp, showDefaultConfirmationPopUp } = use(ConfirmationPopupProviderContext);
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const {
    refetch: readWanderingBoundaries,
    isLoading: readWanderingBoundariesIsLoading,
    data: readWanderingBoundariesData,
    isFetching: readWanderingBoundariesIsFetching,
  } = useLazyReadWanderingBoundaries(selectedLocation?.id ?? '');

  const {
    refetch: readWanderingBoundaryPassages,
    isLoading: readWanderingBoundaryPassagesIsLoading,
    data: readWanderingBoundaryPassagesData,
    isFetching: readWanderingBoundaryPassagesIsFetching,
  } = useLazyReadWanderingBoundaryPassages(selectedLocation?.id ?? '');

  const {
    refetch: readDevices,
    isLoading: readDevicesIsLoading,
    data: readDevicesData,
    isFetching: readDevicesIsFetching,
  } = useLazyReadDevices(selectedLocation?.id ?? '');

  const {
    refetch: readDomipDevices,
    isLoading: readDomipDevicesIsLoading,
    data: readDomipDevicesData,
    isFetching: readDomipDevicesIsFetching,
  } = useLazyReadDomipDevices(selectedLocation?.id ?? '');

  const { mutate: removeWanderingBoundaryMutation } = useRemoveWanderingBoundary();
  const { mutate: createWanderingBoundaryMutation } = useCreateWanderingBoundary();
  const { mutate: updateWanderingBoundaryMutation } = useUpdateWanderingBoundary();
  const { mutate: removeWanderingBoundaryPassageMutation } = useRemoveWanderingBoundaryPassage();
  const { mutate: createWanderingBoundaryPassageMutation } = useCreateWanderingBoundaryPassage();
  const { mutate: updateWanderingBoundaryPassageMutation } = useUpdateWanderingBoundaryPassage();

  useEffect(() => {
    if (selectedLocation?.id) {
      readWanderingBoundaries();
      readWanderingBoundaryPassages();
      readDevices();
      readDomipDevices();
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (readWanderingBoundariesData) {
      setWanderingBoundaries(readWanderingBoundariesData);
      setIsEditing(false);
    }
  }, [readWanderingBoundariesData]);

  useEffect(() => {
    if (readWanderingBoundaryPassagesData) {
      setWanderingBoundaryPassages(readWanderingBoundaryPassagesData);
      setIsEditing(false);
    }
  }, [readWanderingBoundaryPassagesData]);

  useEffect(() => {
    if (readDevicesData) {
      setdetectionLoopDevices(readDevicesData);
    }
  }, [readDevicesData]);

  useEffect(() => {
    if (readDomipDevicesData) {
      setDomipDevices(readDomipDevicesData);
    }
  }, [readDomipDevicesData]);

  const toggleDetails = (location: WanderingBoundaryLocation): void => {
    showDefaultConfirmationPopUp({
      shouldShow: shouldShowConfirmationPopup,
      action: () => setSelectedLocation(selectedLocation?.id === location.id ? undefined : location),
    });
  };

  const addWanderingBoundary = (): void => {
    if (wanderingBoundaries) {
      const newBoundary: WanderingBoundary = {
        id: undefined,
        locationId: selectedLocation?.id ?? '',
        name: '',
        description: '',
        note: '',
        key: uuidv4(),
      };
      setWanderingBoundaries([...wanderingBoundaries, newBoundary]);
      setIsEditing(true);
    }
  };

  const removeWanderingBoundary = (wanderingBoundary: WanderingBoundary): void => {
    if (wanderingBoundary.id) {
      const isLinkedToPassage = wanderingBoundaryPassages?.some(
        (passage) => passage.wanderingBoundaryAId === wanderingBoundary.id || passage.wanderingBoundaryBId === wanderingBoundary.id,
      );

      if (isLinkedToPassage) {
        showConfirmationPopUp(
          () => {},
          t('confirmationPopups.cannotDeleteWanderingBoundary.title', {
            name: wanderingBoundary.name,
          }),
          t('confirmationPopups.cannotDeleteWanderingBoundary.text'),
          t('confirmationPopups.cannotDeleteWanderingBoundary.cancel'),
          '',
        );
        return;
      }

      if (wanderingBoundary.isLinkedToScenario) {
        showConfirmationPopUp(
          () => {},
          t('confirmationPopups.wanderingBoundaryLinkedToScenario.title', {
            name: wanderingBoundary.name,
          }),
          t('confirmationPopups.wanderingBoundaryLinkedToScenario.text'),
          t('confirmationPopups.wanderingBoundaryLinkedToScenario.cancel'),
          '',
        );
        return;
      }

      showConfirmationPopUp(
        () =>
          removeWanderingBoundaryMutation(wanderingBoundary.id!, {
            onSuccess: () => {
              setWanderingBoundaries((current) => current?.filter((b) => b !== wanderingBoundary));
            },
          }),
        t('confirmationPopups.deleteWanderingBoundary.title'),
        t('confirmationPopups.deleteWanderingBoundary.text'),
        t('confirmationPopups.deleteWanderingBoundary.confirm'),
        t('confirmationPopups.deleteWanderingBoundary.cancel'),
      );
    } else if (wanderingBoundaries) {
      const updatedBoundaries = wanderingBoundaries.filter((b) => b !== wanderingBoundary);
      setWanderingBoundaries(updatedBoundaries);
      setIsEditing(false);
    }
  };

  const createWanderingBoundary = (wanderingBoundary: WanderingBoundary): void => {
    createWanderingBoundaryMutation(wanderingBoundary, {
      onSuccess: (createdBoundary) => {
        if (wanderingBoundaries) {
          const updatedBoundaries = wanderingBoundaries.map((b) => (b.id ? b : { ...createdBoundary, key: b.key }));
          setWanderingBoundaries(updatedBoundaries);
          setIsEditing(false);
        }
      },
    });
  };

  const updateWanderingBoundary = (wanderingBoundary: WanderingBoundary): void => {
    updateWanderingBoundaryMutation(wanderingBoundary, {
      onSuccess: (updatedBoundary) => {
        if (wanderingBoundaries) {
          const updatedBoundaries = wanderingBoundaries.map((b) => (b.id === updatedBoundary.id ? { ...updatedBoundary, key: b.key } : b));
          setWanderingBoundaries(updatedBoundaries);
          setIsEditing(false);
        }
      },
    });
  };

  const addWanderingBoundaryPassage = (): void => {
    if (wanderingBoundaryPassages) {
      const newPassage: WanderingBoundaryPassage = {
        id: undefined,
        name: '',
        wanderingBoundaryAId: null,
        wanderingBoundaryBId: null,
        detectionLoopADeviceId: null,
        detectionLoopBDeviceId: null,
        domipADeviceId: null,
        domipBDeviceId: null,
        key: uuidv4(),
        blockingPassageWarningTimeAInMinutes: null,
        blockingPassageWarningTimeBInMinutes: null,
      };
      setWanderingBoundaryPassages([...wanderingBoundaryPassages, newPassage]);
      setIsEditing(true);
    }
  };

  const removeWanderingBoundaryPassage = (wanderingBoundaryPassage: WanderingBoundaryPassage): void => {
    const wanderingBoundaryA = wanderingBoundaries?.find((x) => x.id === wanderingBoundaryPassage.wanderingBoundaryAId);
    const wanderingBoundaryB = wanderingBoundaries?.find((x) => x.id === wanderingBoundaryPassage.wanderingBoundaryBId);
    if (wanderingBoundaryA?.isLinkedToScenario || wanderingBoundaryB?.isLinkedToScenario) {
      showConfirmationPopUp(
        () => {},
        t('confirmationPopups.wanderingBoundaryPassageLinkedToScenario.title', {
          name: wanderingBoundaryPassage.name,
        }),
        t('confirmationPopups.wanderingBoundaryPassageLinkedToScenario.text'),
        t('confirmationPopups.wanderingBoundaryPassageLinkedToScenario.cancel'),
        '',
      );
      return;
    }

    if (wanderingBoundaryPassage.id) {
      showConfirmationPopUp(
        () =>
          removeWanderingBoundaryPassageMutation(wanderingBoundaryPassage.id!, {
            onSuccess: () => {
              setWanderingBoundaryPassages((current) => current?.filter((p) => p !== wanderingBoundaryPassage));
            },
          }),
        t('confirmationPopups.deleteWanderingBoundaryPassage.title'),
        t('confirmationPopups.deleteWanderingBoundaryPassage.text'),
        t('confirmationPopups.deleteWanderingBoundaryPassage.confirm'),
        t('confirmationPopups.deleteWanderingBoundaryPassage.cancel'),
      );
    } else if (wanderingBoundaryPassages) {
      const updatedPassages = wanderingBoundaryPassages.filter((p) => p !== wanderingBoundaryPassage);
      setWanderingBoundaryPassages(updatedPassages);
      setIsEditing(false);
    }
  };

  const createWanderingBoundaryPassage = (wanderingBoundaryPassage: WanderingBoundaryPassage, onError?: () => void): void => {
    createWanderingBoundaryPassageMutation(wanderingBoundaryPassage, {
      onSuccess: (createdPassage) => {
        if (wanderingBoundaryPassages) {
          const updatedPassages = wanderingBoundaryPassages.map((p) => (p.id ? p : { ...createdPassage, key: p.key }));
          setWanderingBoundaryPassages(updatedPassages);
          setIsEditing(false);
        }
      },
      onError: (error: Error) => {
        setErrorMessage(error);
        onError?.();
      },
    });
  };

  const updateWanderingBoundaryPassage = (wanderingBoundaryPassage: WanderingBoundaryPassage, onError?: () => void): void => {
    updateWanderingBoundaryPassageMutation(wanderingBoundaryPassage, {
      onSuccess: (updatedPassage) => {
        if (wanderingBoundaryPassages) {
          const updatedPassages = wanderingBoundaryPassages.map((p) =>
            p.id === updatedPassage.id ? { ...updatedPassage, key: p.key } : p,
          );
          setWanderingBoundaryPassages(updatedPassages);
          setIsEditing(false);
        }
      },
      onError: (error: Error) => {
        setErrorMessage(error);
        onError?.();
      },
    });
  };

  return {
    wanderingBoundaries,
    selectedLocation,
    toggleDetails,
    isLoading:
      readWanderingBoundariesIsLoading ||
      readWanderingBoundariesIsFetching ||
      readWanderingBoundaryPassagesIsLoading ||
      readWanderingBoundaryPassagesIsFetching ||
      readDevicesIsLoading ||
      readDevicesIsFetching ||
      readDomipDevicesIsLoading ||
      readDomipDevicesIsFetching,
    addWanderingBoundary,
    removeWanderingBoundary,
    createWanderingBoundary,
    updateWanderingBoundary,
    isEditing,
    setIsEditing,
    setShouldShowConfirmationPopup,
    wanderingBoundaryPassages,
    addWanderingBoundaryPassage,
    removeWanderingBoundaryPassage,
    createWanderingBoundaryPassage,
    updateWanderingBoundaryPassage,
    detectionLoopDevices,
    domipDevices,
  };
};

export default useWanderingBoundarySettingsDetailsState;
